import { useMemo } from 'react';
import { useOrgStore } from '../store/useOrgStore';
import { TechDebtTable } from '../components/tables/TechDebtTable';
import { StatCard } from '../components/ui/StatCard';
import { EmptyState } from '../components/ui/EmptyState';
import type { TechDebtResult } from '../types/org.types';

// Score is a deductive 0–100 — start at 100, deduct per debt category.
function calcDebtScore(items: { severity: string; count: number }[]): number {
  let score = 100;
  for (const item of items) {
    if (item.count === 0) continue;
    if (item.severity === 'critical') score -= Math.min(25, item.count * 3);
    else if (item.severity === 'high') score -= Math.min(15, item.count * 3);
    else if (item.severity === 'medium') score -= Math.min(10, item.count);
    else if (item.severity === 'low') score -= Math.min(5, Math.floor(item.count / 10));
  }
  return Math.max(0, score);
}

// Live audit store by default, which since TYR-216 is the client Hub alone;
// a stored client scan passes its own audit in.
// Passing `data` at all, null included, is scan mode: the store is not read.
export function TechDebtPage({ data }: { data?: TechDebtResult | null } = {}) {
  const storeTechDebt = useOrgStore((s) => s.audit.techdebt);
  const scanMode = data !== undefined;
  const techdebt = scanMode ? data : storeTechDebt;

  const debtScore = useMemo(() => (techdebt ? calcDebtScore(techdebt.items) : 100), [techdebt]);

  if (!techdebt) return <EmptyState title="No tech debt data. Reconnect to scan again." />;

  const t = techdebt.totals;
  const debtColor = debtScore >= 70 ? 'text-grow' : debtScore >= 50 ? 'text-sunrise' : 'text-sunset';

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between">
        <div>
          <div className="text-xs uppercase tracking-widest text-sunrise mb-1">Sunrise</div>
          <h1 className="text-3xl font-semibold text-crux-light tracking-tight">Tech Debt Analysis</h1>
        </div>
        <div className="text-right">
          <div className="text-[11px] uppercase tracking-widest text-neutral-500">Debt Score</div>
          <div className={`text-3xl font-bold tabular-nums ${debtColor}`}>{debtScore} / 100</div>
        </div>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
        <StatCard label="Old API Classes" value={t.oldApiVersionClasses} accent="sunrise" />
        <StatCard label="Hard-coded IDs" value={t.hardcodedIds} accent="sunset" />
        <StatCard label="Duplicate Labels" value={t.duplicateFieldLabels} accent="sunrise" />
        <StatCard label="Missing Help Text" value={t.fieldsWithoutHelpText} accent="sunrise" />
        <StatCard label="Inactive Flows" value={t.inactiveFlows} accent="sunrise" />
        <StatCard
          label="Aging Fields"
          value={t.fieldsCreatedOver1YearAgo}
          accent="sunrise"
          hint=">365 days old"
        />
      </div>

      <TechDebtTable rows={techdebt.items} />
    </div>
  );
}
